import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { ShoppingCart, Plus, CurrencyInr, TrendUp, TrendDown, CalendarBlank, Truck, ArrowUp, ArrowDown, Trash, ChartBar, Tag, PencilSimple, ListBullets, CaretLeft, CaretRight, CheckCircle, FileText } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;
const formatCurrency = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(v || 0);
const todayStr = () => new Date().toISOString().split("T")[0];
const monthStartStr = () => { const t = todayStr(); return t.slice(0, 8) + "01"; };

/* ====== Margin Card ====== */
function MarginCard({ item }) {
  const isProfit = item.total_profit >= 0;
  return (
    <Card className={`p-3 border bg-white ${isProfit ? "border-green-200" : "border-red-200"}`} data-testid={`margin-${item.product_name}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="font-bold text-sm text-gray-900 truncate">{item.product_name}</p>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isProfit ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
          {isProfit ? "+" : ""}{item.margin_pct}%
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[10px] mt-2">
        <div><span className="text-gray-400">Buy</span><p className="font-bold text-gray-700 font-mono">{formatCurrency(item.avg_buy_rate)}/kg</p></div>
        <div><span className="text-gray-400">Sell</span><p className="font-bold text-blue-600 font-mono">{item.avg_sell_rate > 0 ? `${formatCurrency(item.avg_sell_rate)}/kg` : "-"}</p></div>
        <div><span className="text-gray-400">Profit</span><p className={`font-bold font-mono ${isProfit ? "text-green-600" : "text-red-600"}`}>{formatCurrency(item.total_profit)}</p></div>
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 mt-1 pt-1 border-t border-gray-100">
        <span>Purchased: {item.purchase_kg} KG</span>
        <span>Sold: {item.sales_kg} KG</span>
      </div>
    </Card>
  );
}

/* ====== Main Page ====== */
export default function PurchasePage() {
  const [dashboard, setDashboard] = useState(null);
  const [margins, setMargins] = useState([]);
  const [history, setHistory] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [marginPeriod, setMarginPeriod] = useState("month");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showMultiDialog, setShowMultiDialog] = useState(false);
  const [multiInvoice, setMultiInvoice] = useState({ supplier: "", date: todayStr(), bill_number: "", amount_paid: "", due_date: "", payment_method: "cash" });
  const [multiItems, setMultiItems] = useState([{ product_name: "", kg: "", kilo_price: "", batch_code: "", gst_type: "without_gst", gst_percent: "5", custom_gst: "", expiry_date: "", notes: "", manual_mode: false }]);
  const [showSaleDialog, setShowSaleDialog] = useState(false);
  const [showMultiSaleDialog, setShowMultiSaleDialog] = useState(false);
  const [multiSaleInvoice, setMultiSaleInvoice] = useState({ customer_name: "", customer_contact: "", date: todayStr(), bill_number: "", payment_method: "cash", amount_paid: "", gst_type: "without_gst", gst_percent: "5", custom_gst: "" });
  const [multiSaleItems, setMultiSaleItems] = useState([{ product_name: "", batch_code: "", kg: "", sell_rate: "", notes: "" }]);
  const [showHistory, setShowHistory] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [manualProduct, setManualProduct] = useState(false);
  const [searchProduct, setSearchProduct] = useState("");
  const [searchSupplier, setSearchSupplier] = useState("");
  const [supplierFilter, setSupplierFilter] = useState(""); // for All Suppliers list search

  // Bulk Import
  const [showImportSummary, setShowImportSummary] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [importing, setImporting] = useState(false);
  const importFileRef = useRef(null);
  // Preview → Confirm flow
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // {rows, counters}
  const [committing, setCommitting] = useState(false);

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/purchase/import-template`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "purchase_import_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill it and upload");
    } catch { toast.error("Failed to download template"); }
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/purchase/import/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview(res.data);
      setShowImportPreview(true);
      const { counters } = res.data;
      toast.success(`Reviewed ${counters.total} rows — ${counters.ready + counters.warnings} ready to import`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the file");
    } finally {
      setImporting(false);
      if (importFileRef.current) importFileRef.current.value = "";
    }
  };

  const handleCommitImport = async () => {
    if (!importPreview) return;
    setCommitting(true);
    try {
      const res = await axios.post(`${API}/purchase/import/commit`, { rows: importPreview.rows });
      setImportSummary(res.data);
      setShowImportPreview(false);
      setImportPreview(null);
      setShowImportSummary(true);
      if (res.data.invoices_imported > 0) {
        toast.success(`Imported ${res.data.invoices_imported} invoice(s) · ${res.data.products_imported} product(s)`);
      }
      fetchData(); fetchMargins(); fetchDailyOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  // Supplier drill-down state
  const [supplierDetail, setSupplierDetail] = useState(null);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [showSupplierDialog, setShowSupplierDialog] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null); // {invoice_number, current_paid, grand}

  // Per-product report
  const [report, setReport] = useState(null);
  const [reportFrom, setReportFrom] = useState(monthStartStr());
  const [reportTo, setReportTo] = useState(todayStr());
  const [reportProduct, setReportProduct] = useState("all");
  const [reportLoading, setReportLoading] = useState(false);

  const today = todayStr();
  const [form, setForm] = useState({ product_name: "", kg: "", value: "", kilo_price: "", date: today, batch_code: "", supplier: "", expiry_date: "", notes: "", gst_type: "without_gst", gst_percent: "5", custom_gst: "", amount_paid: "", due_date: "", payment_method: "cash", bill_number: "" });
  const [saleForm, setSaleForm] = useState({ product_name: "", batch_code: "", kg: "", sell_rate: "", value: "", date: today, customer: "", notes: "" });

  // Daily Orders Tracker
  const [orderDate, setOrderDate] = useState(today);
  const [dailyOrders, setDailyOrders] = useState([]);
  const [editOrder, setEditOrder] = useState(null);
  const [editForm, setEditForm] = useState({ product_name: "", kg: "", value: "", kilo_price: "", date: "", batch_code: "", supplier: "", expiry_date: "", notes: "", remarks: "" });
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchDailyOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/purchase/history`, { params: { date_from: orderDate, date_to: orderDate } });
      setDailyOrders(res.data || []);
    } catch (err) { toast.error("Failed to load daily orders"); }
  }, [orderDate]);

  useEffect(() => { fetchDailyOrders(); }, [fetchDailyOrders]);

  const shiftDate = (days) => {
    const d = new Date(orderDate); d.setDate(d.getDate() + days);
    setOrderDate(d.toISOString().split("T")[0]);
  };

  const openEditOrder = (order) => {
    setEditOrder(order);
    setEditForm({
      product_name: order.product_name || "",
      kg: String(order.kg || ""),
      value: String(order.value || ""),
      kilo_price: String(order.kilo_price || ""),
      date: order.date || today,
      batch_code: order.batch_code || "",
      supplier: order.supplier || "",
      expiry_date: order.expiry_date || "",
      notes: order.notes || "",
      remarks: "",
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editOrder) return;
    if (!editForm.remarks?.trim()) { toast.error("Add a remark explaining the edit"); return; }
    const ekg = parseFloat(editForm.kg) || 0;
    const eval_ = parseFloat(editForm.value) || 0;
    try {
      await axios.put(`${API}/inventory/${editOrder.entry_id}`, {
        product_name: editForm.product_name,
        kg: ekg,
        kg_remaining: ekg, // reset remaining on edit
        value: eval_,
        kilo_price: parseFloat(editForm.kilo_price) || (ekg > 0 ? eval_ / ekg : 0),
        date: editForm.date,
        batch_code: editForm.batch_code,
        supplier: editForm.supplier,
        expiry_date: editForm.expiry_date,
        notes: editForm.notes,
        remarks: editForm.remarks,
        entry_type: "purchase",
      });
      toast.success("Order updated");
      setShowEditDialog(false);
      setEditOrder(null);
      fetchData(); fetchMargins(); fetchDailyOrders();
    } catch (err) { toast.error("Update failed"); }
  };

  const handleDeleteOrder = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/inventory/${confirmDelete.entry_id}`);
      toast.success(`Order ${confirmDelete.batch_code || confirmDelete.entry_id} deleted`);
      setConfirmDelete(null);
      fetchData(); fetchMargins(); fetchDailyOrders();
    } catch (err) { toast.error("Delete failed"); }
  };

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, prodRes, batchRes] = await Promise.all([
        axios.get(`${API}/purchase/dashboard`),
        axios.get(`${API}/products`),
        axios.get(`${API}/inventory/batches`),
      ]);
      setDashboard(dashRes.data);
      setProducts(prodRes.data);
      setBatches(batchRes.data);
    } catch (err) { toast.error("Failed to load purchase data"); }
  }, []);

  const fetchMargins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/purchase/margins`, { params: { period: marginPeriod } });
      setMargins(res.data);
    } catch (err) { toast.error("Failed to load margins"); }
  }, [marginPeriod]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchMargins(); }, [fetchMargins]);

  const fetchHistory = async () => {
    try {
      const params = {};
      if (searchProduct) params.product_name = searchProduct;
      if (searchSupplier) params.supplier = searchSupplier;
      const res = await axios.get(`${API}/purchase/history`, { params });
      setHistory(res.data);
      setShowHistory(true);
    } catch (err) { toast.error("Failed to load history"); }
  };

  const fetchReport = useCallback(async () => {
    try {
      setReportLoading(true);
      const params = { date_from: reportFrom, date_to: reportTo };
      if (reportProduct && reportProduct !== "all") params.product_name = reportProduct;
      const res = await axios.get(`${API}/purchase/product-report`, { params });
      setReport(res.data);
    } catch (err) { toast.error("Failed to load report"); }
    finally { setReportLoading(false); }
  }, [reportFrom, reportTo, reportProduct]);

  const openReport = () => { setShowReport(true); fetchReport(); };

  const kg = parseFloat(form.kg) || 0;
  const rateInput = parseFloat(form.kilo_price) || 0;
  const valueInput = parseFloat(form.value) || 0;
  // Smart auto-calc: prefer rate*kg if rate is set, else derive rate from value/kg
  const computedValue = kg > 0 && rateInput > 0 ? +(kg * rateInput).toFixed(2) : valueInput;
  const value = computedValue;
  const autoKiloPrice = kg > 0 && valueInput > 0 ? (valueInput / kg).toFixed(2) : (rateInput > 0 ? rateInput.toFixed(2) : "0.00");
  const effectiveRate = rateInput > 0 ? rateInput : (kg > 0 && valueInput > 0 ? valueInput / kg : 0);

  const handleAddPurchase = async () => {
    if (!form.product_name?.trim()) { toast.error("Enter product name"); return; }
    if (kg <= 0) { toast.error("Enter KG"); return; }
    const gstPct = form.gst_type === "with_gst" ? parseFloat(form.gst_percent === "custom" ? form.custom_gst : form.gst_percent) || 0 : 0;
    try {
      const res = await axios.post(`${API}/inventory/purchase`, {
        product_name: form.product_name.trim(), kg, value,
        kilo_price: parseFloat(form.kilo_price) || parseFloat(autoKiloPrice),
        date: form.date || today, batch_code: form.batch_code, supplier: form.supplier,
        expiry_date: form.expiry_date, notes: form.notes, entry_type: "purchase",
        gst_type: form.gst_type, gst_percent: gstPct,
        bill_number: form.bill_number,
        amount_paid: form.amount_paid === "" ? null : parseFloat(form.amount_paid),
        due_date: form.due_date || "",
        payment_method: form.payment_method || "cash",
      });
      toast.success(`Purchase added! Batch: ${res.data.batch_code} · ${res.data.invoice_payment_status}`);
      setShowAddDialog(false);
      setForm({ product_name: "", kg: "", value: "", kilo_price: "", date: today, batch_code: "", supplier: "", expiry_date: "", notes: "", gst_type: "without_gst", gst_percent: "5", custom_gst: "", amount_paid: "", due_date: "", payment_method: "cash", bill_number: "" });
      fetchData(); fetchMargins(); fetchDailyOrders();
    } catch (err) { toast.error("Failed to add purchase"); }
  };

  const saleKg = parseFloat(saleForm.kg) || 0;
  const saleSellRate = parseFloat(saleForm.sell_rate) || 0;
  const saleValue = parseFloat(saleForm.value) || 0;
  const autoSaleValue = saleKg > 0 && saleSellRate > 0 ? (saleKg * saleSellRate).toFixed(2) : "0.00";
  const selectedBatch = batches.find(b => b.product_name === saleForm.product_name && b.batch_code === saleForm.batch_code);
  const previewProfit = saleValue > 0 && selectedBatch ? (saleValue - (saleKg * (selectedBatch.kilo_price || 0))).toFixed(2) : null;

  const handleAddSale = async () => {
    if (!saleForm.product_name) { toast.error("Select product"); return; }
    if (saleKg <= 0) { toast.error("Enter KG"); return; }
    if (saleSellRate <= 0 && saleValue <= 0) { toast.error("Enter sell rate or total value"); return; }
    try {
      const finalValue = saleValue > 0 ? saleValue : parseFloat(autoSaleValue);
      const finalRate = saleSellRate > 0 ? saleSellRate : (saleKg > 0 ? finalValue / saleKg : 0);
      const res = await axios.post(`${API}/inventory/sale`, {
        product_name: saleForm.product_name,
        batch_code: saleForm.batch_code === "fifo" ? "" : saleForm.batch_code,
        kg: saleKg,
        sell_rate: finalRate,
        value: finalValue,
        date: saleForm.date || today,
        customer: saleForm.customer,
        notes: saleForm.notes || "Sale",
      });
      toast.success(`Sale recorded! Profit: ${formatCurrency(res.data.profit)}`);
      setShowSaleDialog(false);
      setSaleForm({ product_name: "", batch_code: "", kg: "", sell_rate: "", value: "", date: today, customer: "", notes: "" });
      fetchData(); fetchMargins();
      if (showReport) fetchReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record sale"); }
  };

  const openSupplierDetail = async (supplierName) => {
    if (!supplierName) return;
    setShowSupplierDialog(true);
    setSupplierLoading(true);
    setSupplierDetail(null);
    try {
      const res = await axios.get(`${API}/purchase/supplier-detail`, { params: { supplier: supplierName } });
      setSupplierDetail(res.data);
    } catch { toast.error("Failed to load supplier details"); }
    finally { setSupplierLoading(false); }
  };

  const recordSupplierPayment = async (invoice_number, payload) => {
    try {
      await axios.post(`${API}/purchase/invoice-payment-entry`, { invoice_number, ...payload });
      toast.success(`Payment recorded: ₹${payload.amount.toLocaleString("en-IN")} on ${payload.date}`);
      setPayInvoice(null);
      if (supplierDetail?.supplier) openSupplierDetail(supplierDetail.supplier);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Payment record failed"); }
  };

  const deleteSupplierPayment = async (payment_id) => {
    if (!window.confirm("Remove this payment installment? Invoice totals will be re-calculated.")) return;
    try {
      await axios.delete(`${API}/purchase/invoice-payment-entry/${payment_id}`);
      toast.success("Installment removed");
      if (supplierDetail?.supplier) openSupplierDetail(supplierDetail.supplier);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm("Delete this purchase?")) return;
    try { await axios.delete(`${API}/inventory/${entryId}`); toast.success("Deleted"); fetchHistory(); fetchData(); fetchMargins(); } catch (err) { toast.error("Failed"); }
  };

  // Multi-product invoice helpers
  const addLineItem = () => setMultiItems((p) => [...p, { product_name: "", kg: "", kilo_price: "", batch_code: "", gst_type: "without_gst", gst_percent: "5", custom_gst: "", expiry_date: "", notes: "", manual_mode: false }]);
  const removeLineItem = (idx) => setMultiItems((p) => p.length === 1 ? p : p.filter((_, i) => i !== idx));
  const updateLineItem = (idx, field, val) => setMultiItems((p) => p.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const multiTotals = multiItems.reduce((acc, it) => {
    const kgN = parseFloat(it.kg) || 0;
    const rateN = parseFloat(it.kilo_price) || 0;
    const base = +(kgN * rateN).toFixed(2);
    const pct = it.gst_type === "with_gst" ? (parseFloat(it.gst_percent === "custom" ? it.custom_gst : it.gst_percent) || 0) : 0;
    const gst = +(base * pct / 100).toFixed(2);
    return { base: acc.base + base, gst: acc.gst + gst, grand: acc.grand + base + gst };
  }, { base: 0, gst: 0, grand: 0 });

  const submitMultiPurchase = async () => {
    if (!multiInvoice.supplier?.trim()) { toast.error("Supplier required"); return; }
    const validItems = multiItems.filter((it) => it.product_name?.trim() && parseFloat(it.kg) > 0);
    if (validItems.length === 0) { toast.error("Add at least one valid product line"); return; }
    try {
      const payload = {
        supplier: multiInvoice.supplier.trim(),
        date: multiInvoice.date || today,
        bill_number: multiInvoice.bill_number,
        amount_paid: multiInvoice.amount_paid === "" ? null : parseFloat(multiInvoice.amount_paid),
        due_date: multiInvoice.due_date || "",
        payment_method: multiInvoice.payment_method || "cash",
        items: validItems.map((it) => ({
          product_name: it.product_name.trim(),
          kg: parseFloat(it.kg) || 0,
          kilo_price: parseFloat(it.kilo_price) || 0,
          value: (parseFloat(it.kg) || 0) * (parseFloat(it.kilo_price) || 0),
          batch_code: it.batch_code,
          expiry_date: it.expiry_date,
          notes: it.notes,
          gst_type: it.gst_type,
          gst_percent: it.gst_type === "with_gst" ? (parseFloat(it.gst_percent === "custom" ? it.custom_gst : it.gst_percent) || 0) : 0,
        })),
      };
      const res = await axios.post(`${API}/purchase/multi`, payload);
      toast.success(`Invoice ${res.data.invoice_number}: ${res.data.items.length} items (Grand ₹${res.data.totals.grand.toLocaleString("en-IN")}) · ${res.data.invoice_payment_status}`);
      setShowMultiDialog(false);
      fetchData(); fetchMargins(); fetchDailyOrders();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save invoice"); }
  };

  // Multi-Sale helpers
  const addSaleLine = () => setMultiSaleItems((p) => [...p, { product_name: "", batch_code: "", kg: "", sell_rate: "", notes: "" }]);
  const removeSaleLine = (idx) => setMultiSaleItems((p) => p.length === 1 ? p : p.filter((_, i) => i !== idx));
  const updateSaleLine = (idx, field, val) => setMultiSaleItems((p) => p.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const multiSaleBase = multiSaleItems.reduce((sum, it) => sum + ((parseFloat(it.kg) || 0) * (parseFloat(it.sell_rate) || 0)), 0);
  const multiSaleGstPct = multiSaleInvoice.gst_type === "with_gst" ? (parseFloat(multiSaleInvoice.gst_percent === "custom" ? multiSaleInvoice.custom_gst : multiSaleInvoice.gst_percent) || 0) : 0;
  const multiSaleGstAmt = +(multiSaleBase * multiSaleGstPct / 100).toFixed(2);
  const multiSaleGrand = +(multiSaleBase + multiSaleGstAmt).toFixed(2);
  // Estimated profit preview (using FIFO buy cost from batches)
  const estimatedBuyCost = multiSaleItems.reduce((sum, it) => {
    const kgN = parseFloat(it.kg) || 0;
    if (kgN <= 0 || !it.product_name) return sum;
    if (it.batch_code) {
      const b = batches.find(x => x.product_name === it.product_name && x.batch_code === it.batch_code);
      return sum + (kgN * (b?.kilo_price || 0));
    }
    // FIFO estimate
    const prodBatches = batches.filter(b => b.product_name === it.product_name && b.kg_remaining > 0).sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    let need = kgN, cost = 0;
    for (const b of prodBatches) {
      if (need <= 0) break;
      const take = Math.min(need, b.kg_remaining);
      cost += take * (b.kilo_price || 0);
      need -= take;
    }
    return sum + cost;
  }, 0);

  const submitMultiSale = async () => {
    const validItems = multiSaleItems.filter((it) => it.product_name && parseFloat(it.kg) > 0 && parseFloat(it.sell_rate) > 0);
    if (validItems.length === 0) { toast.error("Add at least one valid sale line"); return; }
    try {
      const payload = {
        customer_name: multiSaleInvoice.customer_name,
        customer_contact: multiSaleInvoice.customer_contact,
        date: multiSaleInvoice.date || today,
        bill_number: multiSaleInvoice.bill_number,
        payment_method: multiSaleInvoice.payment_method,
        amount_paid: multiSaleInvoice.amount_paid !== "" ? parseFloat(multiSaleInvoice.amount_paid) : undefined,
        gst_type: multiSaleInvoice.gst_type,
        gst_percent: multiSaleGstPct,
        items: validItems.map((it) => ({
          product_name: it.product_name,
          batch_code: it.batch_code === "fifo" ? "" : it.batch_code,
          kg: parseFloat(it.kg),
          sell_rate: parseFloat(it.sell_rate),
          notes: it.notes,
        })),
      };
      const res = await axios.post(`${API}/inventory/multi-sale`, payload);
      toast.success(`Bill ${res.data.bill_number}: ${res.data.items.length} items · Profit ₹${res.data.totals.profit.toLocaleString("en-IN")}`);
      setShowMultiSaleDialog(false);
      fetchData(); fetchMargins(); fetchDailyOrders();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save sale"); }
  };

  const d = dashboard || { total_purchases: 0, total_purchase_value: 0, total_purchase_kg: 0, today: {}, month: {}, year: {}, top_suppliers: [] };

  const periodCards = [
    { label: "Today", purchases: d.today?.value || 0, sales: d.today?.sales_value || 0, count: d.today?.count || 0 },
    { label: "This Month", purchases: d.month?.value || 0, sales: d.month?.sales_value || 0, count: d.month?.count || 0 },
    { label: "This Year", purchases: d.year?.value || 0, sales: d.year?.sales_value || 0, count: d.year?.count || 0 },
  ];

  // Available batches for the selected product in sale dialog
  const availableBatches = batches.filter(b => b.product_name === saleForm.product_name && b.kg_remaining > 0);

  return (
    <div className="space-y-4 max-w-7xl" data-testid="purchase-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Purchase Dashboard</h1>
          <p className="text-xs font-bold text-indigo-600">{d.total_purchases} purchases · <span className="text-amber-600">{d.total_purchase_kg} KG total</span></p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="text-xs h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={handleDownloadTemplate} data-testid="download-template-btn">
            <FileText size={14} className="mr-1" /> Download Template
          </Button>
          <input ref={importFileRef} type="file" accept=".xlsx,.csv" onChange={handleUploadFile} className="hidden" data-testid="import-file-input" />
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9" onClick={() => importFileRef.current?.click()} disabled={importing} data-testid="upload-excel-btn">
            <Plus size={14} className="mr-1" /> {importing ? "Uploading…" : "Upload Purchase Excel"}
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-9" onClick={openReport} data-testid="open-report-btn">
            <ChartBar size={14} className="mr-1" /> Product Report
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-9" onClick={fetchHistory} data-testid="purchase-history-btn">
            <CalendarBlank size={14} className="mr-1" /> History
          </Button>
          <Button size="sm" variant="outline" className="text-xs h-9 border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => { setSaleForm({ product_name: "", batch_code: "", kg: "", sell_rate: "", value: "", date: today, customer: "", notes: "" }); setShowSaleDialog(true); }} data-testid="add-sale-btn">
            <Tag size={14} className="mr-1" /> Quick Sale
          </Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9" onClick={() => { setMultiSaleInvoice({ customer_name: "", customer_contact: "", date: today, bill_number: "", payment_method: "cash", amount_paid: "", gst_type: "without_gst", gst_percent: "5", custom_gst: "" }); setMultiSaleItems([{ product_name: "", batch_code: "", kg: "", sell_rate: "", notes: "" }]); setShowMultiSaleDialog(true); }} data-testid="multi-sale-btn">
            <Tag size={14} className="mr-1" /> Multi-Product Sale Bill
          </Button>
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-9" onClick={() => { setMultiInvoice({ supplier: "", date: today, bill_number: "", amount_paid: "", due_date: "", payment_method: "cash" }); setMultiItems([{ product_name: "", kg: "", kilo_price: "", batch_code: "", gst_type: "without_gst", gst_percent: "5", custom_gst: "", expiry_date: "", notes: "", manual_mode: false }]); setShowMultiDialog(true); }} data-testid="multi-purchase-btn">
            <Plus size={14} className="mr-1" /> Multi-Product Invoice
          </Button>
          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs h-9" onClick={() => { setForm({ product_name: "", kg: "", value: "", kilo_price: "", date: today, batch_code: "", supplier: "", expiry_date: "", notes: "", gst_type: "without_gst", gst_percent: "5", custom_gst: "", amount_paid: "", due_date: "", payment_method: "cash", bill_number: "" }); setManualProduct(false); setShowAddDialog(true); }} data-testid="add-purchase-btn">
            <Plus size={14} className="mr-1" /> New Purchase
          </Button>
        </div>
      </div>

      {/* Summary Cards — Clickable */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="purchase-summary-cards">
        <button onClick={fetchHistory} className="text-left p-3 border border-indigo-200 bg-white rounded-xl hover:shadow-md hover:scale-[1.02] hover:border-indigo-400 transition-all cursor-pointer" data-testid="card-total-purchases">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0"><ShoppingCart size={18} className="text-indigo-600" weight="bold" /></div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-wide">Total Purchases</p>
              <p className="text-xl sm:text-2xl font-black text-gray-900 leading-tight" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{d.total_purchases}</p>
            </div>
          </div>
          <p className="text-[10px] text-indigo-500 mt-1 font-semibold">VIEW HISTORY →</p>
        </button>
        <button onClick={fetchHistory} className="text-left p-3 border border-green-200 bg-white rounded-xl hover:shadow-md hover:scale-[1.02] hover:border-green-400 transition-all cursor-pointer" data-testid="card-total-spend">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0"><CurrencyInr size={18} className="text-green-600" weight="bold" /></div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold text-green-700 uppercase tracking-wide">Total Spend</p>
              <p className="text-base sm:text-xl font-black text-green-700 leading-tight truncate" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(d.total_purchase_value)}</p>
            </div>
          </div>
          <p className="text-[10px] text-green-600 mt-1 font-semibold">VIEW DETAILS →</p>
        </button>
        <button onClick={openReport} className="text-left p-3 border border-blue-200 bg-white rounded-xl hover:shadow-md hover:scale-[1.02] hover:border-blue-400 transition-all cursor-pointer" data-testid="card-suppliers">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0"><Truck size={18} className="text-blue-600" weight="bold" /></div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold text-blue-700 uppercase tracking-wide">Suppliers</p>
              <p className="text-xl sm:text-2xl font-black text-gray-900 leading-tight" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{d.top_suppliers?.length || 0}</p>
            </div>
          </div>
          <p className="text-[10px] text-blue-600 mt-1 font-semibold">VIEW REPORT →</p>
        </button>
        <button onClick={openReport} className="text-left p-3 border border-amber-200 bg-white rounded-xl hover:shadow-md hover:scale-[1.02] hover:border-amber-400 transition-all cursor-pointer" data-testid="card-total-kg">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0"><TrendUp size={18} className="text-amber-600" weight="bold" /></div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold text-amber-700 uppercase tracking-wide">Total KG</p>
              <p className="text-xl sm:text-2xl font-black text-amber-700 leading-tight" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{d.total_purchase_kg}</p>
            </div>
          </div>
          <p className="text-[10px] text-amber-600 mt-1 font-semibold">VIEW PRODUCTS →</p>
        </button>
      </div>

      {/* GST Cards (Input GST tracking) */}
      {d.gst && (
        <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="gst-cards">
          <Card className="p-3 border-2 border-gray-200 bg-gray-50">
            <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">Without GST</p>
            <p className="text-base sm:text-xl font-black text-gray-700 font-mono">{formatCurrency(d.gst.purchases_without_gst)}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">No tax billed</p>
          </Card>
          <Card className="p-3 border-2 border-blue-200 bg-blue-50">
            <p className="text-[11px] font-bold text-blue-700 uppercase tracking-wide">With GST (Base)</p>
            <p className="text-base sm:text-xl font-black text-blue-700 font-mono">{formatCurrency(d.gst.purchases_with_gst)}</p>
            <p className="text-[10px] text-blue-600 mt-0.5">Pre-GST amount</p>
          </Card>
          <Card className="p-3 border-2 border-violet-200 bg-violet-50">
            <p className="text-[11px] font-bold text-violet-700 uppercase tracking-wide">Input GST</p>
            <p className="text-base sm:text-xl font-black text-violet-700 font-mono">{formatCurrency(d.gst.input_gst)}</p>
            <p className="text-[10px] text-violet-600 mt-0.5">GST paid on purchases</p>
          </Card>
        </div>
      )}

      {/* Period Reports: Daily / Monthly / Yearly */}
      <div>
        <h2 className="text-base font-black text-gray-900 mb-2 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
          <span className="w-1 h-5 bg-indigo-600 rounded-full"></span>
          Purchase vs Sales Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {periodCards.map((p) => {
            const profit = p.sales - p.purchases;
            const isProfit = profit >= 0;
            return (
              <Card key={p.label} className="p-4 border border-gray-200 bg-white hover:shadow-md transition-all" data-testid={`period-${p.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-black text-gray-900 uppercase tracking-wide" style={{ fontFamily: "'Gupter', serif" }}>{p.label}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isProfit ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {isProfit ? "PROFIT" : "LOSS"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold text-red-700 uppercase">Purchases ({p.count})</p>
                    <p className="text-lg font-black text-red-600 font-mono">{formatCurrency(p.purchases)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] font-bold text-green-700 uppercase">Sales</p>
                    <p className="text-lg font-black text-green-600 font-mono">{formatCurrency(p.sales)}</p>
                  </div>
                </div>
                <div className={`mt-3 pt-2 border-t flex items-center justify-between ${isProfit ? "border-green-200" : "border-red-200"}`}>
                  <span className="text-xs font-black text-gray-700 uppercase">Margin</span>
                  <span className={`text-base font-black font-mono ${isProfit ? "text-green-700" : "text-red-700"}`}>
                    {isProfit ? <TrendUp size={14} className="inline mr-0.5" weight="bold" /> : <TrendDown size={14} className="inline mr-0.5" weight="bold" />}
                    {formatCurrency(Math.abs(profit))}
                  </span>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* ====== Daily Orders Tracker ====== */}
      <Card className="border border-gray-200 bg-white" data-testid="daily-orders-tracker">
        <div className="flex items-center justify-between p-3 border-b border-gray-100 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
              <ListBullets size={18} className="text-indigo-600" weight="bold" />
            </div>
            <div>
              <h2 className="text-base font-black text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
                <span className="w-1 h-5 bg-indigo-600 rounded-full"></span>
                Daily Orders Tracker
              </h2>
              <p className="text-[11px] text-gray-600 font-semibold ml-3">Follow each purchase order one by one</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => shiftDate(-1)} data-testid="prev-day-btn"><CaretLeft size={16} /></Button>
            <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className="h-8 text-xs w-[150px]" data-testid="order-date-picker" />
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => shiftDate(1)} data-testid="next-day-btn" disabled={orderDate >= today}><CaretRight size={16} /></Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setOrderDate(today)} data-testid="today-btn">Today</Button>
          </div>
        </div>
        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-0 border-b border-gray-100 bg-gradient-to-r from-indigo-50 via-amber-50 to-green-50">
          <div className="p-3 text-center border-r border-gray-200">
            <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider">Orders</p>
            <p className="text-xl font-black text-indigo-700 font-mono" data-testid="daily-order-count">{dailyOrders.length}</p>
          </div>
          <div className="p-3 text-center border-r border-gray-200">
            <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Total KG</p>
            <p className="text-xl font-black text-amber-700 font-mono" data-testid="daily-total-kg">{dailyOrders.reduce((s, o) => s + (o.kg || 0), 0).toFixed(1)}</p>
          </div>
          <div className="p-3 text-center">
            <p className="text-[10px] font-bold text-green-700 uppercase tracking-wider">Total Value</p>
            <p className="text-xl font-black text-green-700 font-mono" data-testid="daily-total-value">{formatCurrency(dailyOrders.reduce((s, o) => s + (o.value || 0), 0))}</p>
          </div>
        </div>
        {/* Orders list */}
        {dailyOrders.length === 0 ? (
          <div className="p-8 text-center" data-testid="no-orders">
            <ShoppingCart size={36} className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No orders on {orderDate}</p>
            <p className="text-[11px] text-gray-400">Click &quot;New Purchase&quot; to add one</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100" data-testid="daily-orders-list">
            {dailyOrders.map((o, idx) => (
              <div key={o.entry_id} className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors" data-testid={`order-row-${o.entry_id}`}>
                {/* Order # */}
                <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-black flex-shrink-0">
                  {idx + 1}
                </div>
                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-sm text-gray-900 truncate">{o.product_name}</p>
                    {o.batch_code && (
                      <span className="px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-indigo-50 text-indigo-600 border border-indigo-200" data-testid={`order-batch-${o.entry_id}`}>{o.batch_code}</span>
                    )}
                    {o.supplier && (
                      <span className="px-1.5 py-0.5 rounded-md text-[10px] bg-blue-50 text-blue-600 border border-blue-200">
                        <Truck size={9} className="inline mr-0.5" />{o.supplier}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 flex-wrap">
                    <span className="font-mono"><b className="text-gray-700">{o.kg}</b> KG</span>
                    <span>•</span>
                    <span className="font-mono">{formatCurrency(o.kilo_price)}/kg</span>
                    {o.expiry_date && (<><span>•</span><span className="text-amber-600">Exp: {o.expiry_date}</span></>)}
                    {o.notes && (<><span>•</span><span className="italic truncate max-w-[200px]">{o.notes}</span></>)}
                  </div>
                </div>
                {/* Value */}
                <div className="text-right flex-shrink-0 hidden sm:block">
                  <p className="text-base font-black text-green-600 font-mono" data-testid={`order-value-${o.entry_id}`}>{formatCurrency(o.value)}</p>
                  <p className="text-[10px] text-gray-400">order total</p>
                </div>
                {/* Actions */}
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-indigo-600 hover:bg-indigo-50" onClick={() => openEditOrder(o)} data-testid={`edit-order-${o.entry_id}`} title="Edit order">
                    <PencilSimple size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500 hover:bg-red-50" onClick={() => setConfirmDelete(o)} data-testid={`delete-order-${o.entry_id}`} title="Delete order">
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Selling Margins per Product */}
      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h2 className="text-base font-black text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
            <span className="w-1 h-5 bg-green-600 rounded-full"></span>
            Selling Margins (per product)
          </h2>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
            {[{ k: "daily", l: "Daily" }, { k: "month", l: "Monthly" }, { k: "yearly", l: "Yearly" }].map((t) => (
              <Button key={t.k} size="sm" variant={marginPeriod === t.k ? "default" : "ghost"}
                className={`h-7 text-[11px] ${marginPeriod === t.k ? "bg-indigo-600 text-white" : "text-gray-600"}`}
                onClick={() => setMarginPeriod(t.k)} data-testid={`margin-${t.k}`}
              >{t.l}</Button>
            ))}
          </div>
        </div>
        {margins.length === 0 ? (
          <Card className="p-6 text-center border border-gray-200 bg-white"><p className="text-sm text-gray-400">No margin data for this period. Record some sales to see profit.</p></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="margins-grid">
            {margins.map((m) => <MarginCard key={m.product_name} item={m} />)}
          </div>
        )}
      </div>

      {/* All Suppliers */}
      {d.top_suppliers?.length > 0 && (() => {
        const filtered = d.top_suppliers.filter((s) => !supplierFilter || (s.name || "Unknown").toLowerCase().includes(supplierFilter.toLowerCase()));
        const totalPending = d.top_suppliers.reduce((acc, s) => acc + (s.balance_due || 0), 0);
        const totalPendingCount = d.top_suppliers.reduce((acc, s) => acc + (s.pending_count || 0), 0);
        return (
        <Card className="p-3 border border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <h3 className="text-base font-black text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
              <span className="w-1 h-5 bg-blue-600 rounded-full"></span>
              All Suppliers <span className="text-xs font-bold text-gray-500">({d.top_suppliers.length})</span>
              {totalPendingCount > 0 && <span className="text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{formatCurrency(totalPending)} pending · {totalPendingCount} invoices</span>}
            </h3>
            <Input
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              placeholder="🔍 Search supplier…"
              className="h-8 text-xs w-48"
              data-testid="supplier-filter"
            />
          </div>
          <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1" data-testid="supplier-list">
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-400 italic text-center py-4">No suppliers match &quot;{supplierFilter}&quot;</p>
            ) : filtered.map((s, i) => {
              const displayName = s.name || "Unknown / No supplier";
              const hasPending = (s.balance_due || 0) > 0;
              return (
                <button key={`sup-${i}-${s.name}`} onClick={() => openSupplierDetail(s.name)} className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-blue-50 hover:border-blue-300 border border-transparent rounded-lg text-xs transition-all text-left" data-testid={`supplier-row-${i}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                    <span className={`font-semibold truncate ${s.name ? "text-gray-900 hover:text-blue-700" : "text-gray-400 italic"}`}>{displayName}</span>
                    <span className="text-gray-400 flex-shrink-0">{s.count} orders</span>
                    {hasPending && <span className="text-[9px] font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded flex-shrink-0">Due {formatCurrency(s.balance_due)} · {s.pending_count}</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-bold text-gray-700 font-mono">{formatCurrency(s.total)}</span>
                    <span className="text-[10px] text-blue-600 font-bold">VIEW →</span>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
        );
      })()}

      {/* ====== Add Purchase Dialog ====== */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Gupter', serif" }}>New Purchase Entry</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-sm font-semibold">Product Name</Label>
                <button type="button" onClick={() => { setManualProduct(!manualProduct); setForm(p => ({ ...p, product_name: "" })); }} className="text-[11px] text-indigo-600 font-bold hover:underline" data-testid="toggle-manual">
                  {manualProduct ? "Select from list" : "Add manually"}
                </button>
              </div>
              {manualProduct ? (
                <Input value={form.product_name} onChange={(e) => setForm(p => ({ ...p, product_name: e.target.value }))} placeholder="Type product name" className="h-11" data-testid="purchase-manual-product" autoFocus />
              ) : (
                <Select value={form.product_name} onValueChange={(v) => setForm(p => ({ ...p, product_name: v }))}>
                  <SelectTrigger className="h-11" data-testid="purchase-product"><SelectValue placeholder="Select Product" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.product_id} value={p.product_name}>{p.product_name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">KG (Quantity)</Label><Input type="number" min="0" step="0.1" value={form.kg} onChange={(e) => setForm(p => ({ ...p, kg: e.target.value, value: "" }))} placeholder="e.g. 100" className="h-11" data-testid="purchase-kg" /></div>
              <div><Label className="text-sm font-semibold">Purchase Date</Label><Input type="date" value={form.date} onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))} className="h-11" data-testid="purchase-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold">Rate/KG (₹)</Label>
                <Input type="number" min="0" step="0.01" value={form.kilo_price} onChange={(e) => setForm(p => ({ ...p, kilo_price: e.target.value, value: "" }))} placeholder="e.g. 250" className="h-11" data-testid="purchase-rate" />
                <p className="text-[10px] text-gray-500 mt-0.5">Type KG + Rate → auto Total</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Total Value (₹)</Label>
                <Input type="number" min="0" step="0.01" value={form.value !== "" ? form.value : (computedValue > 0 ? computedValue.toFixed(2) : "")} onChange={(e) => setForm(p => ({ ...p, value: e.target.value }))} placeholder="auto-calc" className="h-11 bg-green-50 font-bold" data-testid="purchase-value" />
                <p className="text-[10px] text-green-700 mt-0.5 font-bold">Auto: {formatCurrency(computedValue)}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Batch Code</Label><Input value={form.batch_code} onChange={(e) => setForm(p => ({ ...p, batch_code: e.target.value }))} placeholder="Auto if empty" className="h-10 bg-gray-50" data-testid="purchase-batch" /></div>
              <div><Label className="text-sm font-semibold">Supplier</Label><Input value={form.supplier} onChange={(e) => setForm(p => ({ ...p, supplier: e.target.value }))} placeholder="Supplier name" className="h-10" data-testid="purchase-supplier" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Expiry Date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm(p => ({ ...p, expiry_date: e.target.value }))} className="h-10" data-testid="purchase-expiry" /></div>
              <div><Label className="text-sm font-semibold">Notes</Label><Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Info..." className="h-10" data-testid="purchase-notes" /></div>
            </div>
            {/* GST Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              <Label className="text-sm font-bold text-blue-700">GST Details</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setForm(p => ({ ...p, gst_type: "without_gst" }))} className={`h-9 rounded-lg text-xs font-bold border-2 transition-all ${form.gst_type === "without_gst" ? "bg-gray-700 text-white border-gray-700" : "bg-white text-gray-600 border-gray-300"}`} data-testid="gst-without">Without GST</button>
                <button type="button" onClick={() => setForm(p => ({ ...p, gst_type: "with_gst" }))} className={`h-9 rounded-lg text-xs font-bold border-2 transition-all ${form.gst_type === "with_gst" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-600 border-blue-300"}`} data-testid="gst-with">With GST</button>
              </div>
              {form.gst_type === "with_gst" && (
                <div className="grid grid-cols-7 gap-1">
                  {["0", "5", "12", "18", "28", "custom"].map((p) => (
                    <button key={p} type="button" onClick={() => setForm(f => ({ ...f, gst_percent: p }))} className={`h-8 rounded text-[11px] font-bold border ${form.gst_percent === p ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-600 border-blue-200"}`} data-testid={`gst-pct-${p}`}>{p === "custom" ? "Custom" : `${p}%`}</button>
                  ))}
                  {form.gst_percent === "custom" && (
                    <Input type="number" min="0" max="100" placeholder="%" value={form.custom_gst} onChange={(e) => setForm(p => ({ ...p, custom_gst: e.target.value }))} className="h-8 text-xs col-span-1" data-testid="purchase-custom-gst" />
                  )}
                </div>
              )}
            </div>
            {/* Live Total Preview */}
            {(() => {
              const baseAmt = value;
              const gstPct = form.gst_type === "with_gst" ? (parseFloat(form.gst_percent === "custom" ? form.custom_gst : form.gst_percent) || 0) : 0;
              const gstAmt = +(baseAmt * gstPct / 100).toFixed(2);
              const grandTotal = +(baseAmt + gstAmt).toFixed(2);
              return (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200 text-sm space-y-1" data-testid="purchase-total-preview">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Amount ({kg > 0 ? `${kg} KG @ ${formatCurrency(effectiveRate)}/kg` : "-"}):</span>
                    <span className="font-bold text-gray-900 font-mono">{formatCurrency(baseAmt)}</span>
                  </div>
                  {form.gst_type === "with_gst" && (
                    <div className="flex justify-between">
                      <span className="text-blue-700 font-semibold">+ GST ({gstPct}%):</span>
                      <span className="font-bold text-blue-700 font-mono" data-testid="purchase-gst-amount">{formatCurrency(gstAmt)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1 border-t border-green-300">
                    <span className="text-green-800 font-black uppercase text-xs">Grand Total:</span>
                    <span className="font-black text-green-700 text-base font-mono" data-testid="purchase-grand-total">{formatCurrency(grandTotal)}</span>
                  </div>
                </div>
              );
            })()}
            {/* Supplier Payment block (single purchase) */}
            {(() => {
              const baseAmt = value;
              const gstPct = form.gst_type === "with_gst" ? (parseFloat(form.gst_percent === "custom" ? form.custom_gst : form.gst_percent) || 0) : 0;
              const grandTotal = +(baseAmt + baseAmt * gstPct / 100).toFixed(2);
              const paidN = parseFloat(form.amount_paid) || 0;
              const balanceN = Math.max(grandTotal - paidN, 0);
              const statusLabel = form.amount_paid === "" ? "Fully Paid" : (paidN >= grandTotal && grandTotal > 0 ? "Paid" : (paidN > 0 ? "Partial" : "Pending"));
              const statusColor = statusLabel === "Pending" ? "bg-red-100 text-red-700" : statusLabel === "Partial" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
              return (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 space-y-2" data-testid="purchase-payment-block">
                  <Label className="text-sm font-bold text-amber-700">Supplier Payment</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px] text-green-700 font-bold">Amount Paid to Supplier</Label><Input type="number" min="0" value={form.amount_paid} onChange={(e) => setForm(p => ({ ...p, amount_paid: e.target.value }))} placeholder="0 = fully pending" className="h-9 text-sm border-green-300" data-testid="purchase-amount-paid" /></div>
                    <div><Label className="text-[10px] text-orange-700 font-bold">Payment Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm(p => ({ ...p, due_date: e.target.value }))} className="h-9 text-sm border-orange-300" data-testid="purchase-due-date" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px] text-gray-600 font-bold">Payment Method</Label>
                      <select value={form.payment_method} onChange={(e) => setForm(p => ({ ...p, payment_method: e.target.value }))} className="h-9 text-sm border rounded w-full bg-white px-2" data-testid="purchase-payment-method">
                        <option value="cash">Cash</option><option value="upi">UPI</option><option value="bank">Bank</option><option value="cheque">Cheque</option><option value="credit">Credit</option>
                      </select>
                    </div>
                    <div><Label className="text-[10px] text-gray-600 font-bold">Bill / Invoice #</Label><Input value={form.bill_number} onChange={(e) => setForm(p => ({ ...p, bill_number: e.target.value }))} placeholder="Auto if empty" className="h-9 text-sm" data-testid="purchase-bill-number" /></div>
                  </div>
                  <div className="flex items-center justify-between text-xs pt-1 border-t border-amber-200">
                    <span className="text-amber-800">Balance Outstanding:</span>
                    <span className="font-mono font-bold text-amber-900">{formatCurrency(balanceN)} <span className={`ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${statusColor}`} data-testid="purchase-payment-status">{statusLabel}</span></span>
                  </div>
                </div>
              );
            })()}
            <Button onClick={handleAddPurchase} className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-bold" data-testid="save-purchase-btn">
              <Plus size={16} className="mr-1" /> Save Purchase
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Record Sale Dialog ====== */}
      <Dialog open={showSaleDialog} onOpenChange={setShowSaleDialog}>
        <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Gupter', serif" }}>Record Sale</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-semibold">Product</Label>
              <Select value={saleForm.product_name} onValueChange={(v) => setSaleForm(p => ({ ...p, product_name: v, batch_code: "" }))}>
                <SelectTrigger className="h-11" data-testid="sale-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>
                  {[...new Set(batches.filter(b => b.kg_remaining > 0).map(b => b.product_name))].map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold">Batch (Buy Rate determines profit)</Label>
              <Select value={saleForm.batch_code} onValueChange={(v) => setSaleForm(p => ({ ...p, batch_code: v }))}>
                <SelectTrigger className="h-11" data-testid="sale-batch"><SelectValue placeholder="Auto FIFO (oldest first)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fifo">Auto FIFO (oldest batch first)</SelectItem>
                  {availableBatches.map((b) => (
                    <SelectItem key={b.batch_code} value={b.batch_code}>
                      {b.batch_code} | {b.kg_remaining} KG @ {formatCurrency(b.kilo_price)}/kg
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedBatch && (
                <p className="text-[10px] text-gray-500 mt-1">Buy rate: <b className="font-mono">{formatCurrency(selectedBatch.kilo_price)}/kg</b> | Available: <b>{selectedBatch.kg_remaining} KG</b></p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">KG Sold</Label><Input type="number" min="0" step="0.1" value={saleForm.kg} onChange={(e) => setSaleForm(p => ({ ...p, kg: e.target.value }))} placeholder="e.g. 50" className="h-11" data-testid="sale-kg" /></div>
              <div><Label className="text-sm font-semibold">Sale Date</Label><Input type="date" value={saleForm.date} onChange={(e) => setSaleForm(p => ({ ...p, date: e.target.value }))} className="h-11" data-testid="sale-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Sell Rate (₹/kg)</Label><Input type="number" min="0" step="0.01" value={saleForm.sell_rate} onChange={(e) => setSaleForm(p => ({ ...p, sell_rate: e.target.value, value: "" }))} placeholder="e.g. 220" className="h-11" data-testid="sale-rate" /></div>
              <div><Label className="text-sm font-semibold">Total Value</Label><Input type="number" min="0" step="0.01" value={saleForm.value || autoSaleValue} onChange={(e) => setSaleForm(p => ({ ...p, value: e.target.value }))} className="h-11 bg-gray-50" data-testid="sale-value" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Customer</Label><Input value={saleForm.customer} onChange={(e) => setSaleForm(p => ({ ...p, customer: e.target.value }))} placeholder="Customer name" className="h-10" data-testid="sale-customer" /></div>
              <div><Label className="text-sm font-semibold">Notes</Label><Input value={saleForm.notes} onChange={(e) => setSaleForm(p => ({ ...p, notes: e.target.value }))} placeholder="Bill no., remarks..." className="h-10" data-testid="sale-notes" /></div>
            </div>
            {previewProfit !== null && saleKg > 0 && (
              <div className={`rounded-lg p-3 border text-sm ${parseFloat(previewProfit) >= 0 ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid="sale-preview">
                <div className="flex justify-between"><span className="text-gray-600">Buy Cost:</span><span className="font-mono text-gray-700">{formatCurrency(saleKg * (selectedBatch?.kilo_price || 0))}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Sale Value:</span><span className="font-mono text-blue-700">{formatCurrency(saleValue || parseFloat(autoSaleValue))}</span></div>
                <div className={`flex justify-between font-bold pt-1 mt-1 border-t ${parseFloat(previewProfit) >= 0 ? "text-green-700 border-green-200" : "text-red-700 border-red-200"}`}>
                  <span>Profit:</span><span className="font-mono">{formatCurrency(parseFloat(previewProfit))}</span>
                </div>
              </div>
            )}
            <Button onClick={handleAddSale} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold" data-testid="save-sale-btn">
              <Tag size={16} className="mr-1" /> Save Sale
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Per-Product Report Dialog ====== */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Gupter', serif" }}>Per-Product Purchase vs Sale Report</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 bg-gray-50 p-3 rounded-lg border">
              <div><Label className="text-[10px] text-gray-500">From</Label><Input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} className="h-9 text-xs" data-testid="report-from" /></div>
              <div><Label className="text-[10px] text-gray-500">To</Label><Input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} className="h-9 text-xs" data-testid="report-to" /></div>
              <div>
                <Label className="text-[10px] text-gray-500">Product</Label>
                <Select value={reportProduct} onValueChange={setReportProduct}>
                  <SelectTrigger className="h-9 text-xs" data-testid="report-product"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {products.map((p) => <SelectItem key={p.product_id} value={p.product_name}>{p.product_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end"><Button size="sm" onClick={fetchReport} className="h-9 w-full bg-indigo-600 text-white" data-testid="apply-report-btn">Apply Filters</Button></div>
            </div>

            {/* Quick range buttons */}
            <div className="flex gap-1 flex-wrap">
              {[
                { l: "Today", from: today, to: today },
                { l: "This Week", from: new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0], to: today },
                { l: "This Month", from: monthStartStr(), to: today },
                { l: "This Year", from: today.slice(0, 5) + "01-01", to: today },
              ].map((r) => (
                <Button key={r.l} size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => { setReportFrom(r.from); setReportTo(r.to); setTimeout(fetchReport, 50); }} data-testid={`quick-${r.l.toLowerCase().replace(/\s/g, "-")}`}>{r.l}</Button>
              ))}
            </div>

            {/* Totals */}
            {report?.totals && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="report-totals">
                <Card className="p-2.5 border border-red-200 bg-red-50">
                  <p className="text-[10px] text-red-600">Purchase Value</p>
                  <p className="text-base font-bold text-red-700 font-mono">{formatCurrency(report.totals.purchase_value)}</p>
                  <p className="text-[10px] text-gray-500">{report.totals.purchase_kg} KG</p>
                </Card>
                <Card className="p-2.5 border border-blue-200 bg-blue-50">
                  <p className="text-[10px] text-blue-600">Sale Value</p>
                  <p className="text-base font-bold text-blue-700 font-mono">{formatCurrency(report.totals.sale_value)}</p>
                  <p className="text-[10px] text-gray-500">{report.totals.sale_kg} KG</p>
                </Card>
                <Card className={`p-2.5 border ${report.totals.total_profit >= 0 ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
                  <p className={`text-[10px] ${report.totals.total_profit >= 0 ? "text-green-600" : "text-red-600"}`}>Total Profit</p>
                  <p className={`text-base font-bold font-mono ${report.totals.total_profit >= 0 ? "text-green-700" : "text-red-700"}`}>{formatCurrency(report.totals.total_profit)}</p>
                </Card>
                <Card className="p-2.5 border border-gray-200 bg-white">
                  <p className="text-[10px] text-gray-500">Margin %</p>
                  <p className="text-base font-bold text-gray-900 font-mono">
                    {report.totals.sale_value > 0 ? ((report.totals.total_profit / report.totals.sale_value) * 100).toFixed(1) : 0}%
                  </p>
                </Card>
              </div>
            )}

            {/* Product table */}
            {reportLoading ? (
              <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}</div>
            ) : !report || report.products.length === 0 ? (
              <Card className="p-6 text-center"><p className="text-sm text-gray-400">No data for this date range</p></Card>
            ) : (
              <div className="overflow-x-auto" data-testid="report-table">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold text-gray-600">Product</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-600">Purch KG</th>
                      <th className="px-2 py-2 text-right font-semibold text-red-600">Purch Value</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-600">Sale KG</th>
                      <th className="px-2 py-2 text-right font-semibold text-blue-600">Sale Value</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-600">Buy/kg</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-600">Sell/kg</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-600">Margin/kg</th>
                      <th className="px-2 py-2 text-right font-semibold text-green-600">Profit</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-600">Mgn%</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {report.products.map((p) => (
                      <tr key={p.product_name} className="hover:bg-gray-50" data-testid={`report-row-${p.product_name}`}>
                        <td className="px-2 py-2 font-semibold text-gray-900">{p.product_name}</td>
                        <td className="px-2 py-2 text-right font-mono">{p.purchase_kg}</td>
                        <td className="px-2 py-2 text-right font-mono text-red-600">{formatCurrency(p.purchase_value)}</td>
                        <td className="px-2 py-2 text-right font-mono">{p.sale_kg}</td>
                        <td className="px-2 py-2 text-right font-mono text-blue-600">{formatCurrency(p.sale_value)}</td>
                        <td className="px-2 py-2 text-right font-mono">{formatCurrency(p.avg_buy_rate)}</td>
                        <td className="px-2 py-2 text-right font-mono">{p.avg_sell_rate > 0 ? formatCurrency(p.avg_sell_rate) : "-"}</td>
                        <td className="px-2 py-2 text-right font-mono">{p.margin_per_kg !== 0 ? formatCurrency(p.margin_per_kg) : "-"}</td>
                        <td className={`px-2 py-2 text-right font-mono font-bold ${p.total_profit >= 0 ? "text-green-600" : "text-red-600"}`}>{formatCurrency(p.total_profit)}</td>
                        <td className={`px-2 py-2 text-right font-mono font-bold ${p.margin_pct >= 0 ? "text-green-600" : "text-red-600"}`}>{p.margin_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Purchase History Dialog ====== */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="sm:max-w-[650px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-bold">Purchase History</DialogTitle></DialogHeader>
          <div className="flex gap-2 mb-3">
            <Input placeholder="Product..." value={searchProduct} onChange={(e) => setSearchProduct(e.target.value)} className="h-8 text-xs" />
            <Input placeholder="Supplier..." value={searchSupplier} onChange={(e) => setSearchSupplier(e.target.value)} className="h-8 text-xs" />
            <Button size="sm" onClick={fetchHistory} className="h-8 text-xs bg-indigo-600 text-white">Filter</Button>
          </div>
          <div className="space-y-1.5">
            {history.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No purchases found</p>
            ) : history.map((h) => (
              <div key={h.entry_id} className="flex items-center justify-between p-2.5 bg-green-50 rounded-lg border border-green-200 text-xs" data-testid={`hist-${h.entry_id}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <ArrowUp size={14} className="text-green-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-bold text-gray-900">{h.product_name}</p>
                      {h.batch_code && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-mono bg-indigo-50 text-indigo-600 border border-indigo-200">{h.batch_code}</span>}
                    </div>
                    <p className="text-gray-500">{h.date} {h.supplier ? `| ${h.supplier}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="text-right">
                    <p className="font-bold font-mono text-green-600">+{h.kg} KG</p>
                    <p className="text-gray-500 font-mono">{formatCurrency(h.value)} ({formatCurrency(h.kilo_price)}/kg)</p>
                  </div>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => deleteEntry(h.entry_id)}><Trash size={12} /></Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* ====== Edit Order Dialog ====== */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Gupter', serif" }}>Edit Purchase Order</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-semibold">Product Name</Label>
              <Input value={editForm.product_name} onChange={(e) => setEditForm(p => ({ ...p, product_name: e.target.value }))} className="h-11" data-testid="edit-product-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">KG</Label><Input type="number" min="0" step="0.1" value={editForm.kg} onChange={(e) => setEditForm(p => ({ ...p, kg: e.target.value, kilo_price: parseFloat(e.target.value) > 0 ? (parseFloat(p.value || 0) / parseFloat(e.target.value)).toFixed(2) : p.kilo_price }))} className="h-11" data-testid="edit-kg" /></div>
              <div><Label className="text-sm font-semibold">Date</Label><Input type="date" value={editForm.date} onChange={(e) => setEditForm(p => ({ ...p, date: e.target.value }))} className="h-11" data-testid="edit-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Total Value</Label><Input type="number" min="0" value={editForm.value} onChange={(e) => setEditForm(p => ({ ...p, value: e.target.value, kilo_price: parseFloat(p.kg || 0) > 0 ? (parseFloat(e.target.value) / parseFloat(p.kg)).toFixed(2) : p.kilo_price }))} className="h-11" data-testid="edit-value" /></div>
              <div><Label className="text-sm font-semibold">Rate/KG</Label><Input type="number" min="0" value={editForm.kilo_price} onChange={(e) => setEditForm(p => ({ ...p, kilo_price: e.target.value }))} className="h-11 bg-gray-50" data-testid="edit-rate" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Batch Code</Label><Input value={editForm.batch_code} onChange={(e) => setEditForm(p => ({ ...p, batch_code: e.target.value }))} className="h-10" data-testid="edit-batch-code" /></div>
              <div><Label className="text-sm font-semibold">Supplier</Label><Input value={editForm.supplier} onChange={(e) => setEditForm(p => ({ ...p, supplier: e.target.value }))} className="h-10" data-testid="edit-supplier" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Expiry Date</Label><Input type="date" value={editForm.expiry_date} onChange={(e) => setEditForm(p => ({ ...p, expiry_date: e.target.value }))} className="h-10" data-testid="edit-expiry" /></div>
              <div><Label className="text-sm font-semibold">Notes</Label><Input value={editForm.notes} onChange={(e) => setEditForm(p => ({ ...p, notes: e.target.value }))} className="h-10" data-testid="edit-notes" /></div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-orange-700">Edit Remarks * <span className="text-[10px] text-orange-500">(why are you editing?)</span></Label>
              <Input value={editForm.remarks} onChange={(e) => setEditForm(p => ({ ...p, remarks: e.target.value }))} placeholder="e.g. Wrong KG entered, supplier corrected..." className="h-10 border-orange-200 focus:border-orange-400" data-testid="edit-remarks" />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-700">
              <b>Note:</b> Editing will reset the batch&apos;s remaining KG. Any prior sales from this batch may need re-checking.
            </div>
            <Button onClick={handleSaveEdit} className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="save-edit-order-btn">
              <PencilSimple size={16} className="mr-1" /> Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Delete Confirm Dialog ====== */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle className="text-lg font-bold text-red-600" style={{ fontFamily: "'Gupter', serif" }}>Delete this purchase?</DialogTitle></DialogHeader>
          {confirmDelete && (
            <div className="space-y-3 py-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Product:</span>
                  <span className="font-bold text-gray-900">{confirmDelete.product_name}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Quantity:</span>
                  <span className="font-mono font-bold">{confirmDelete.kg} KG</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Value:</span>
                  <span className="font-mono font-bold text-green-700">{formatCurrency(confirmDelete.value)}</span>
                </div>
                {confirmDelete.batch_code && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Batch:</span>
                    <span className="font-mono text-indigo-600">{confirmDelete.batch_code}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-red-600">This action cannot be undone. Stock summary and margin reports will recalculate automatically.</p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 h-10" onClick={() => setConfirmDelete(null)} data-testid="cancel-delete-btn">Cancel</Button>
                <Button className="flex-1 h-10 bg-red-600 hover:bg-red-700 text-white font-bold" onClick={handleDeleteOrder} data-testid="confirm-delete-btn">
                  <Trash size={14} className="mr-1" /> Delete Permanently
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* ====== Multi-Product Invoice Dialog ====== */}
      <Dialog open={showMultiDialog} onOpenChange={setShowMultiDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[92vh] overflow-y-auto" data-testid="multi-purchase-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
              <ShoppingCart size={18} className="text-purple-600" weight="bold" /> Multi-Product Purchase Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Invoice header */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 bg-purple-50 p-3 rounded-lg border border-purple-200">
              <div>
                <Label className="text-xs font-bold text-purple-700">Supplier *</Label>
                <Input value={multiInvoice.supplier} onChange={(e) => setMultiInvoice((p) => ({ ...p, supplier: e.target.value }))} placeholder="Supplier name" className="h-9 text-sm" data-testid="multi-supplier" />
              </div>
              <div>
                <Label className="text-xs font-bold text-purple-700">Invoice / Bill #</Label>
                <Input value={multiInvoice.bill_number} onChange={(e) => setMultiInvoice((p) => ({ ...p, bill_number: e.target.value }))} placeholder="Auto if empty" className="h-9 text-sm" data-testid="multi-bill" />
              </div>
              <div>
                <Label className="text-xs font-bold text-purple-700">Date *</Label>
                <Input type="date" value={multiInvoice.date} onChange={(e) => setMultiInvoice((p) => ({ ...p, date: e.target.value }))} className="h-9 text-sm" data-testid="multi-date" />
              </div>
            </div>

            {/* Line items */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-2 flex items-center justify-between">
                <h4 className="text-xs font-black text-gray-700 uppercase">Line Items ({multiItems.length})</h4>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={addLineItem} data-testid="add-line-item">
                  <Plus size={12} className="mr-0.5" /> Add Product
                </Button>
              </div>
              <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                {multiItems.map((it, idx) => {
                  const kgN = parseFloat(it.kg) || 0;
                  const rateN = parseFloat(it.kilo_price) || 0;
                  const base = +(kgN * rateN).toFixed(2);
                  const pct = it.gst_type === "with_gst" ? (parseFloat(it.gst_percent === "custom" ? it.custom_gst : it.gst_percent) || 0) : 0;
                  const gst = +(base * pct / 100).toFixed(2);
                  const grand = +(base + gst).toFixed(2);
                  return (
                    <div key={idx} className="p-2 bg-white hover:bg-gray-50" data-testid={`line-item-${idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-black flex items-center justify-center">{idx + 1}</span>
                        {it.manual_mode ? (
                          <div className="flex-1 flex items-center gap-1">
                            <Input value={it.product_name} onChange={(e) => updateLineItem(idx, "product_name", e.target.value)} placeholder="Type new product name" className="h-8 text-xs flex-1 border-amber-300 bg-amber-50" data-testid={`li-product-manual-${idx}`} autoFocus />
                            <button onClick={() => { updateLineItem(idx, "manual_mode", false); updateLineItem(idx, "product_name", ""); }} className="h-7 px-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 text-[10px] font-bold flex items-center gap-0.5" title="Back to product list" data-testid={`li-back-to-list-${idx}`}>← List</button>
                          </div>
                        ) : (
                          <select
                            value={it.product_name}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "__manual__") {
                                updateLineItem(idx, "manual_mode", true);
                                updateLineItem(idx, "product_name", "");
                              } else {
                                updateLineItem(idx, "product_name", v);
                              }
                            }}
                            className="h-8 text-xs border rounded flex-1 bg-white px-2"
                            data-testid={`li-product-${idx}`}
                          >
                            <option value="">Select product</option>
                            <option value="__manual__" className="font-bold text-amber-700">✏️ Add new product manually…</option>
                            {it.product_name && !products.some((p) => p.product_name === it.product_name) && <option value={it.product_name}>{it.product_name} (custom)</option>}
                            {products.map((p) => <option key={p.product_id} value={p.product_name}>{p.product_name}</option>)}
                          </select>
                        )}
                        <Input value={it.batch_code} onChange={(e) => updateLineItem(idx, "batch_code", e.target.value)} placeholder="Batch (auto)" className="h-8 text-xs w-32" data-testid={`li-batch-${idx}`} />
                        <button onClick={() => removeLineItem(idx)} disabled={multiItems.length === 1} className="h-7 w-7 rounded bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-30 flex items-center justify-center" title="Remove" data-testid={`li-remove-${idx}`}>
                          <Trash size={12} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-6 gap-1.5">
                        <div>
                          <Label className="text-[9px] text-gray-500">KG</Label>
                          <Input type="number" min="0" step="0.1" value={it.kg} onChange={(e) => updateLineItem(idx, "kg", e.target.value)} placeholder="0" className="h-8 text-xs" data-testid={`li-kg-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[9px] text-gray-500">Rate/kg</Label>
                          <Input type="number" min="0" step="0.01" value={it.kilo_price} onChange={(e) => updateLineItem(idx, "kilo_price", e.target.value)} placeholder="0" className="h-8 text-xs" data-testid={`li-rate-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[9px] text-gray-500">Base Amount</Label>
                          <div className="h-8 px-2 flex items-center bg-gray-50 rounded border text-xs font-mono font-bold text-gray-900" data-testid={`li-base-${idx}`}>{formatCurrency(base)}</div>
                        </div>
                        <div>
                          <Label className="text-[9px] text-gray-500">GST</Label>
                          <select value={it.gst_type === "without_gst" ? "none" : (it.gst_percent || "5")} onChange={(e) => {
                            const v = e.target.value;
                            if (v === "none") updateLineItem(idx, "gst_type", "without_gst");
                            else { updateLineItem(idx, "gst_type", "with_gst"); updateLineItem(idx, "gst_percent", v); }
                          }} className="h-8 text-xs border rounded w-full bg-white" data-testid={`li-gst-${idx}`}>
                            <option value="none">No GST</option>
                            <option value="0">0%</option>
                            <option value="5">5%</option>
                            <option value="12">12%</option>
                            <option value="18">18%</option>
                            <option value="28">28%</option>
                            <option value="custom">Custom</option>
                          </select>
                          {it.gst_type === "with_gst" && it.gst_percent === "custom" && (
                            <Input type="number" placeholder="%" value={it.custom_gst} onChange={(e) => updateLineItem(idx, "custom_gst", e.target.value)} className="h-7 text-[10px] mt-0.5" data-testid={`li-custom-gst-${idx}`} />
                          )}
                        </div>
                        <div>
                          <Label className="text-[9px] text-gray-500">GST ₹</Label>
                          <div className="h-8 px-2 flex items-center bg-blue-50 rounded border text-xs font-mono text-blue-700" data-testid={`li-gstamt-${idx}`}>{formatCurrency(gst)}</div>
                        </div>
                        <div>
                          <Label className="text-[9px] text-gray-500">Grand Total</Label>
                          <div className="h-8 px-2 flex items-center bg-green-50 rounded border text-xs font-mono font-black text-green-700" data-testid={`li-grand-${idx}`}>{formatCurrency(grand)}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-1.5">
                        <Input type="date" value={it.expiry_date} onChange={(e) => updateLineItem(idx, "expiry_date", e.target.value)} placeholder="Expiry" className="h-7 text-[11px]" data-testid={`li-expiry-${idx}`} />
                        <Input value={it.notes} onChange={(e) => updateLineItem(idx, "notes", e.target.value)} placeholder="Notes (opt)" className="h-7 text-[11px]" data-testid={`li-notes-${idx}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Invoice totals */}
            <div className="grid grid-cols-3 gap-2" data-testid="multi-totals">
              <Card className="p-2.5 border-2 border-gray-200 bg-gray-50">
                <p className="text-[10px] font-bold text-gray-700 uppercase">Base Total</p>
                <p className="text-base font-black text-gray-900 font-mono">{formatCurrency(multiTotals.base)}</p>
              </Card>
              <Card className="p-2.5 border-2 border-blue-200 bg-blue-50">
                <p className="text-[10px] font-bold text-blue-700 uppercase">Total GST</p>
                <p className="text-base font-black text-blue-700 font-mono">{formatCurrency(multiTotals.gst)}</p>
              </Card>
              <Card className="p-2.5 border-2 border-green-300 bg-green-50">
                <p className="text-[10px] font-bold text-green-700 uppercase">Grand Total</p>
                <p className="text-base font-black text-green-700 font-mono">{formatCurrency(multiTotals.grand)}</p>
              </Card>
            </div>

            {/* Supplier Payment block */}
            {(() => {
              const paidN = parseFloat(multiInvoice.amount_paid) || 0;
              const balanceN = Math.max(multiTotals.grand - paidN, 0);
              const statusLabel = multiInvoice.amount_paid === ""
                ? "Fully Paid"
                : (paidN >= multiTotals.grand && multiTotals.grand > 0 ? "Paid" : (paidN > 0 ? "Partial" : "Pending"));
              const statusColor = statusLabel === "Pending" ? "bg-red-100 text-red-700" : statusLabel === "Partial" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
              return (
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 bg-amber-50 p-3 rounded-lg border-2 border-amber-200" data-testid="multi-payment-row">
                  <div>
                    <Label className="text-xs font-bold text-emerald-700">Total Bill Amount</Label>
                    <div className="h-9 px-2 flex items-center bg-emerald-50 border-2 border-emerald-300 rounded text-sm font-mono font-black text-emerald-700" data-testid="multi-total-bill">{formatCurrency(multiTotals.grand)}</div>
                    <p className="text-[10px] text-emerald-600 mt-0.5">From items + GST</p>
                  </div>
                  <div>
                    <Label className="text-xs font-bold text-green-700">Amount Paid to Supplier</Label>
                    <Input type="number" min="0" value={multiInvoice.amount_paid} onChange={(e) => setMultiInvoice((p) => ({ ...p, amount_paid: e.target.value }))} placeholder="0 = fully pending" className="h-9 text-sm border-green-300" data-testid="multi-amount-paid" />
                    <p className="text-[10px] text-green-600 mt-0.5">Empty = fully paid</p>
                  </div>
                  <div>
                    <Label className="text-xs font-bold text-orange-700">Payment Due Date</Label>
                    <Input type="date" value={multiInvoice.due_date} onChange={(e) => setMultiInvoice((p) => ({ ...p, due_date: e.target.value }))} className="h-9 text-sm border-orange-300" data-testid="multi-due-date" />
                    <p className="text-[10px] text-orange-600 mt-0.5">Balance: <b>{formatCurrency(balanceN)}</b> <span className={`ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${statusColor}`}>{statusLabel}</span></p>
                  </div>
                  <div>
                    <Label className="text-xs font-bold text-gray-700">Payment Method</Label>
                    <select value={multiInvoice.payment_method} onChange={(e) => setMultiInvoice((p) => ({ ...p, payment_method: e.target.value }))} className="h-9 text-sm border rounded w-full bg-white px-2" data-testid="multi-payment-method">
                      <option value="cash">Cash</option><option value="upi">UPI</option><option value="bank">Bank</option><option value="cheque">Cheque</option><option value="credit">Credit</option>
                    </select>
                  </div>
                </div>
              );
            })()}

            <Button className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white font-bold" onClick={submitMultiPurchase} data-testid="save-multi-purchase">
              <CheckCircle size={16} className="mr-1" weight="bold" /> Save Invoice ({multiItems.filter(i => i.product_name && parseFloat(i.kg) > 0).length} items → Inventory)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* ====== Multi-Product Sale Bill Dialog ====== */}
      <Dialog open={showMultiSaleDialog} onOpenChange={setShowMultiSaleDialog}>
        <DialogContent className="sm:max-w-[950px] max-h-[92vh] overflow-y-auto" data-testid="multi-sale-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
              <Tag size={18} className="text-blue-600" weight="bold" /> Multi-Product Sale Bill
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Bill header */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div><Label className="text-xs font-bold text-blue-700">Customer *</Label><Input value={multiSaleInvoice.customer_name} onChange={(e) => setMultiSaleInvoice((p) => ({ ...p, customer_name: e.target.value }))} placeholder="Customer name" className="h-9 text-sm" data-testid="ms-customer" /></div>
              <div><Label className="text-xs font-bold text-blue-700">Phone</Label><Input value={multiSaleInvoice.customer_contact} onChange={(e) => setMultiSaleInvoice((p) => ({ ...p, customer_contact: e.target.value }))} placeholder="Phone" className="h-9 text-sm" data-testid="ms-phone" /></div>
              <div><Label className="text-xs font-bold text-blue-700">Bill #</Label><Input value={multiSaleInvoice.bill_number} onChange={(e) => setMultiSaleInvoice((p) => ({ ...p, bill_number: e.target.value }))} placeholder="Auto if empty" className="h-9 text-sm" data-testid="ms-bill" /></div>
              <div><Label className="text-xs font-bold text-blue-700">Date *</Label><Input type="date" value={multiSaleInvoice.date} onChange={(e) => setMultiSaleInvoice((p) => ({ ...p, date: e.target.value }))} className="h-9 text-sm" data-testid="ms-date" /></div>
            </div>

            {/* Line items */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-2 flex items-center justify-between">
                <h4 className="text-xs font-black text-gray-700 uppercase">Sale Items ({multiSaleItems.length})</h4>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={addSaleLine} data-testid="add-sale-line">
                  <Plus size={12} className="mr-0.5" /> Add Item
                </Button>
              </div>
              <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                {multiSaleItems.map((it, idx) => {
                  const kgN = parseFloat(it.kg) || 0;
                  const rateN = parseFloat(it.sell_rate) || 0;
                  const lineBase = +(kgN * rateN).toFixed(2);
                  const availBatches = batches.filter(b => b.product_name === it.product_name && b.kg_remaining > 0);
                  const selectedBatch = availBatches.find(b => b.batch_code === it.batch_code);
                  const lineBuy = selectedBatch ? kgN * (selectedBatch.kilo_price || 0) : 0;
                  const lineProfit = lineBase - lineBuy;
                  return (
                    <div key={idx} className="p-2 bg-white hover:bg-gray-50" data-testid={`sale-line-${idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-black flex items-center justify-center">{idx + 1}</span>
                        <select value={it.product_name} onChange={(e) => { updateSaleLine(idx, "product_name", e.target.value); updateSaleLine(idx, "batch_code", ""); }} className="h-8 text-xs border rounded flex-1 bg-white px-2" data-testid={`sl-product-${idx}`}>
                          <option value="">Select Product</option>
                          {[...new Set(batches.filter(b => b.kg_remaining > 0).map(b => b.product_name))].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <select value={it.batch_code} onChange={(e) => updateSaleLine(idx, "batch_code", e.target.value)} className="h-8 text-xs border rounded w-48 bg-white px-2" data-testid={`sl-batch-${idx}`}>
                          <option value="fifo">Auto FIFO</option>
                          {availBatches.map((b) => <option key={b.batch_code} value={b.batch_code}>{b.batch_code} · {b.kg_remaining}kg @ {formatCurrency(b.kilo_price)}</option>)}
                        </select>
                        <button onClick={() => removeSaleLine(idx)} disabled={multiSaleItems.length === 1} className="h-7 w-7 rounded bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-30 flex items-center justify-center" data-testid={`sl-remove-${idx}`}>
                          <Trash size={12} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                        <div>
                          <Label className="text-[9px] text-gray-500">KG</Label>
                          <Input type="number" min="0" step="0.1" value={it.kg} onChange={(e) => updateSaleLine(idx, "kg", e.target.value)} placeholder="0" className="h-8 text-xs" data-testid={`sl-kg-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[9px] text-gray-500">Sell Rate/kg</Label>
                          <Input type="number" min="0" step="0.01" value={it.sell_rate} onChange={(e) => updateSaleLine(idx, "sell_rate", e.target.value)} placeholder="0" className="h-8 text-xs" data-testid={`sl-rate-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[9px] text-gray-500">Line Total</Label>
                          <div className="h-8 px-2 flex items-center bg-blue-50 rounded border text-xs font-mono font-bold text-blue-700" data-testid={`sl-total-${idx}`}>{formatCurrency(lineBase)}</div>
                        </div>
                        <div>
                          <Label className="text-[9px] text-gray-500">Buy Cost</Label>
                          <div className="h-8 px-2 flex items-center bg-gray-50 rounded border text-xs font-mono text-gray-700">{selectedBatch ? formatCurrency(lineBuy) : "FIFO"}</div>
                        </div>
                        <div>
                          <Label className="text-[9px] text-gray-500">Est. Profit</Label>
                          <div className={`h-8 px-2 flex items-center rounded border text-xs font-mono font-black ${lineProfit >= 0 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`} data-testid={`sl-profit-${idx}`}>{selectedBatch ? formatCurrency(lineProfit) : "—"}</div>
                        </div>
                      </div>
                      <Input value={it.notes} onChange={(e) => updateSaleLine(idx, "notes", e.target.value)} placeholder="Notes (opt)" className="h-7 text-[11px] mt-1.5" data-testid={`sl-notes-${idx}`} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* GST + payment */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 bg-gray-50 p-3 rounded-lg border">
              <div>
                <Label className="text-xs font-bold text-gray-700">GST Bill?</Label>
                <div className="flex gap-1 mt-1">
                  <button type="button" onClick={() => setMultiSaleInvoice((p) => ({ ...p, gst_type: "without_gst" }))} className={`h-8 px-2 rounded text-[10px] font-bold flex-1 ${multiSaleInvoice.gst_type !== "with_gst" ? "bg-gray-700 text-white" : "bg-white border border-gray-300 text-gray-600"}`} data-testid="ms-no-gst">Not GST</button>
                  <button type="button" onClick={() => setMultiSaleInvoice((p) => ({ ...p, gst_type: "with_gst" }))} className={`h-8 px-2 rounded text-[10px] font-bold flex-1 ${multiSaleInvoice.gst_type === "with_gst" ? "bg-blue-600 text-white" : "bg-white border border-blue-300 text-blue-600"}`} data-testid="ms-with-gst">GST Bill</button>
                </div>
                {multiSaleInvoice.gst_type === "with_gst" && (
                  <div className="flex gap-1 flex-wrap mt-1">
                    {["0", "5", "12", "18", "28"].map((p) => (
                      <button key={p} type="button" onClick={() => setMultiSaleInvoice((s) => ({ ...s, gst_percent: p }))} className={`h-6 px-2 rounded text-[10px] font-bold ${multiSaleInvoice.gst_percent === p ? "bg-blue-600 text-white" : "bg-white border border-blue-200 text-blue-600"}`} data-testid={`ms-gst-${p}`}>{p}%</button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-bold text-gray-700">Amount Paid (opt)</Label>
                <Input type="number" value={multiSaleInvoice.amount_paid} onChange={(e) => setMultiSaleInvoice((p) => ({ ...p, amount_paid: e.target.value }))} placeholder="Defaults to Grand Total" className="h-9 text-sm" data-testid="ms-paid" />
              </div>
              <div>
                <Label className="text-xs font-bold text-gray-700">Payment Method</Label>
                <select value={multiSaleInvoice.payment_method} onChange={(e) => setMultiSaleInvoice((p) => ({ ...p, payment_method: e.target.value }))} className="h-9 text-sm border rounded w-full bg-white px-2" data-testid="ms-method">
                  <option value="cash">Cash</option><option value="upi">UPI</option><option value="card">Card</option><option value="bank">Bank</option><option value="cheque">Cheque</option>
                </select>
              </div>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="ms-totals">
              <Card className="p-2.5 border-2 border-gray-200 bg-gray-50">
                <p className="text-[10px] font-bold text-gray-700 uppercase">Base Total</p>
                <p className="text-base font-black text-gray-900 font-mono">{formatCurrency(multiSaleBase)}</p>
              </Card>
              <Card className="p-2.5 border-2 border-blue-200 bg-blue-50">
                <p className="text-[10px] font-bold text-blue-700 uppercase">GST ({multiSaleGstPct}%)</p>
                <p className="text-base font-black text-blue-700 font-mono">{formatCurrency(multiSaleGstAmt)}</p>
              </Card>
              <Card className="p-2.5 border-2 border-emerald-300 bg-emerald-50">
                <p className="text-[10px] font-bold text-emerald-700 uppercase">Grand Total</p>
                <p className="text-base font-black text-emerald-700 font-mono">{formatCurrency(multiSaleGrand)}</p>
              </Card>
              <Card className={`p-2.5 border-2 ${(multiSaleBase - estimatedBuyCost) >= 0 ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}`}>
                <p className={`text-[10px] font-bold uppercase ${(multiSaleBase - estimatedBuyCost) >= 0 ? "text-green-700" : "text-red-700"}`}>Est. Profit</p>
                <p className={`text-base font-black font-mono ${(multiSaleBase - estimatedBuyCost) >= 0 ? "text-green-700" : "text-red-700"}`}>{formatCurrency(multiSaleBase - estimatedBuyCost)}</p>
              </Card>
            </div>

            <Button className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold" onClick={submitMultiSale} data-testid="save-multi-sale">
              <CheckCircle size={16} className="mr-1" weight="bold" /> Save Sale Bill ({multiSaleItems.filter(i => i.product_name && parseFloat(i.kg) > 0 && parseFloat(i.sell_rate) > 0).length} items)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Supplier Detail Dialog ====== */}
      <Dialog open={showSupplierDialog} onOpenChange={(o) => { if (!o) { setShowSupplierDialog(false); setSupplierDetail(null); setPayInvoice(null); } }}>
        <DialogContent className="sm:max-w-[1000px] max-h-[92vh] overflow-y-auto" data-testid="supplier-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
              <Truck size={18} className="text-indigo-600" weight="bold" /> Supplier: {supplierDetail?.supplier || "—"}
            </DialogTitle>
          </DialogHeader>
          {supplierLoading && <p className="text-sm text-gray-400 italic text-center py-8">Loading…</p>}
          {!supplierLoading && supplierDetail && (
            <div className="space-y-4">
              {/* Top stats */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <Card className="p-2.5 border-2 border-indigo-200 bg-indigo-50"><p className="text-[10px] font-bold text-indigo-700 uppercase">Invoices</p><p className="text-lg font-black text-indigo-700 font-mono">{supplierDetail.totals.invoice_count}</p></Card>
                <Card className="p-2.5 border-2 border-gray-200 bg-gray-50"><p className="text-[10px] font-bold text-gray-700 uppercase">Total KG</p><p className="text-lg font-black text-gray-800 font-mono">{supplierDetail.totals.total_kg}</p></Card>
                <Card className="p-2.5 border-2 border-purple-300 bg-purple-50"><p className="text-[10px] font-bold text-purple-700 uppercase">Total Spend</p><p className="text-lg font-black text-purple-700 font-mono">{formatCurrency(supplierDetail.totals.total_grand)}</p></Card>
                <Card className="p-2.5 border-2 border-emerald-300 bg-emerald-50"><p className="text-[10px] font-bold text-emerald-700 uppercase">Paid</p><p className="text-lg font-black text-emerald-700 font-mono">{formatCurrency(supplierDetail.totals.total_paid)}</p></Card>
                <Card className={`p-2.5 border-2 ${supplierDetail.totals.total_balance > 0 ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"}`}>
                  <p className={`text-[10px] font-bold uppercase ${supplierDetail.totals.total_balance > 0 ? "text-red-700" : "text-gray-500"}`}>Balance Due</p>
                  <p className={`text-lg font-black font-mono ${supplierDetail.totals.total_balance > 0 ? "text-red-700" : "text-gray-500"}`}>{formatCurrency(supplierDetail.totals.total_balance)}</p>
                  {supplierDetail.totals.pending_count > 0 && <p className="text-[9px] text-red-600 font-bold">{supplierDetail.totals.pending_count} pending invoices</p>}
                </Card>
              </div>

              {/* Products purchased from this supplier */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-indigo-100 p-2 flex items-center justify-between">
                  <h4 className="text-xs font-black text-indigo-800 uppercase">Products Purchased ({supplierDetail.products.length})</h4>
                </div>
                <div className="grid grid-cols-12 gap-1 px-2 py-1 text-[10px] font-black text-gray-500 uppercase bg-gray-50 border-b">
                  <div className="col-span-1">#</div>
                  <div className="col-span-5">Product</div>
                  <div className="col-span-2 text-right">Total KG</div>
                  <div className="col-span-2 text-right">Total Spent</div>
                  <div className="col-span-2 text-right">Orders</div>
                </div>
                {supplierDetail.products.map((p, i) => (
                  <div key={p.product_name} className="grid grid-cols-12 gap-1 px-2 py-1 items-center border-b border-gray-100 text-xs" data-testid={`supplier-product-${i}`}>
                    <div className="col-span-1 text-gray-500 font-mono">{i + 1}</div>
                    <div className="col-span-5 font-semibold text-gray-900 truncate">{p.product_name}</div>
                    <div className="col-span-2 text-right font-mono">{p.kg}kg</div>
                    <div className="col-span-2 text-right font-mono font-bold text-purple-700">{formatCurrency(p.grand)}</div>
                    <div className="col-span-2 text-right text-gray-500">{p.orders}</div>
                  </div>
                ))}
              </div>

              {/* Invoices */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-amber-100 p-2 flex items-center justify-between">
                  <h4 className="text-xs font-black text-amber-800 uppercase">Invoices & Payments ({supplierDetail.invoices.length})</h4>
                </div>
                <div className="divide-y divide-gray-100 max-h-[360px] overflow-y-auto">
                  {supplierDetail.invoices.map((inv) => {
                    const isPay = payInvoice?.invoice_number === inv.invoice_number;
                    const statusColor = inv.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : inv.payment_status === "partial" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
                    return (
                      <div key={inv.invoice_number} className="p-2.5 bg-white" data-testid={`supplier-invoice-${inv.invoice_number}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="font-mono font-bold text-indigo-700 text-xs truncate">{inv.invoice_number}</span>
                            <span className="text-[10px] text-gray-500">{inv.date}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${statusColor}`}>{inv.payment_status}</span>
                            <span className="text-[10px] text-gray-500">{inv.items.length} items</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-mono text-gray-700">Total <b>{formatCurrency(inv.grand_total)}</b></span>
                            <span className="font-mono text-emerald-700">Paid <b>{formatCurrency(inv.amount_paid)}</b></span>
                            {inv.balance > 0 && <span className="font-mono text-red-700">Bal <b>{formatCurrency(inv.balance)}</b></span>}
                            {inv.payment_status !== "paid" && (
                              <button onClick={() => setPayInvoice({ invoice_number: inv.invoice_number, grand: inv.grand_total, balance: inv.balance, amount: String(inv.balance), date: today, payment_method: inv.payment_method || "cash", remarks: "" })} className="px-2 h-7 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold text-[10px] border border-emerald-200" data-testid={`record-pay-${inv.invoice_number}`}>
                                + Add Payment
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Items mini-list */}
                        <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {inv.items.map((it) => (
                            <div key={it.entry_id} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded text-[11px]">
                              <span className="text-gray-800 truncate"><b>{it.product_name}</b> · {it.kg}kg @ {formatCurrency(it.kilo_price)}/kg <span className="text-indigo-600 font-mono ml-1">[{it.batch_code}]</span></span>
                              <span className="font-mono font-bold text-purple-700">{formatCurrency(it.grand_total)}</span>
                            </div>
                          ))}
                        </div>
                        {/* Payment history */}
                        {(inv.payments || []).length > 0 && (
                          <div className="mt-2 border border-emerald-200 rounded bg-emerald-50/50 overflow-hidden" data-testid={`payments-history-${inv.invoice_number}`}>
                            <div className="bg-emerald-100 px-2 py-1 text-[10px] font-black text-emerald-800 uppercase">Payment History ({inv.payments.length})</div>
                            <div className="divide-y divide-emerald-100">
                              {inv.payments.map((p) => (
                                <div key={p.payment_id} className="flex items-center justify-between px-2 py-1 text-[11px]" data-testid={`payment-${p.payment_id}`}>
                                  <span className="text-gray-800">
                                    <b className="text-emerald-800">{p.date}</b>
                                    <span className="font-mono font-bold text-emerald-700 ml-2">{formatCurrency(p.amount)}</span>
                                    <span className="ml-2 px-1.5 py-0.5 rounded bg-white text-gray-600 text-[9px] uppercase font-bold border">{p.payment_method}</span>
                                    {p.remarks && <span className="ml-2 text-gray-500 italic">&quot;{p.remarks}&quot;</span>}
                                  </span>
                                  <button onClick={() => deleteSupplierPayment(p.payment_id)} className="h-6 w-6 rounded text-red-500 hover:bg-red-100 flex items-center justify-center" title="Remove this installment" data-testid={`del-payment-${p.payment_id}`}>
                                    <Trash size={11} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Inline payment record form */}
                        {isPay && (
                          <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded grid grid-cols-1 sm:grid-cols-5 gap-2 items-end" data-testid={`pay-form-${inv.invoice_number}`}>
                            <div>
                              <Label className="text-[10px] font-bold text-emerald-700">Payment Date *</Label>
                              <Input type="date" value={payInvoice.date} onChange={(e) => setPayInvoice((p) => ({ ...p, date: e.target.value }))} className="h-8 text-xs" data-testid="pay-date-input" />
                            </div>
                            <div>
                              <Label className="text-[10px] font-bold text-emerald-700">Amount *</Label>
                              <Input type="number" min="0" max={payInvoice.balance} step="0.01" value={payInvoice.amount} onChange={(e) => setPayInvoice((p) => ({ ...p, amount: e.target.value }))} className="h-8 text-xs" data-testid="pay-amount-input" />
                              <p className="text-[9px] text-gray-500 mt-0.5">Max {formatCurrency(payInvoice.balance)}</p>
                            </div>
                            <div>
                              <Label className="text-[10px] font-bold text-gray-700">Method</Label>
                              <select value={payInvoice.payment_method} onChange={(e) => setPayInvoice((p) => ({ ...p, payment_method: e.target.value }))} className="h-8 text-xs border rounded w-full bg-white px-2">
                                <option value="cash">Cash</option><option value="upi">UPI</option><option value="bank">Bank</option><option value="cheque">Cheque</option>
                              </select>
                            </div>
                            <div>
                              <Label className="text-[10px] font-bold text-gray-700">Remarks</Label>
                              <Input value={payInvoice.remarks} onChange={(e) => setPayInvoice((p) => ({ ...p, remarks: e.target.value }))} placeholder="e.g. UPI #123" className="h-8 text-xs" />
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-8 text-[11px] flex-1" onClick={() => setPayInvoice(null)}>Cancel</Button>
                              <Button size="sm" className="h-8 text-[11px] flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" onClick={() => {
                                const amt = parseFloat(payInvoice.amount) || 0;
                                if (amt <= 0) { toast.error("Enter a positive amount"); return; }
                                if (!payInvoice.date) { toast.error("Pick a date"); return; }
                                recordSupplierPayment(inv.invoice_number, { date: payInvoice.date, amount: amt, payment_method: payInvoice.payment_method, remarks: payInvoice.remarks });
                              }} data-testid="pay-submit-btn">Save</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ====== Import PREVIEW Dialog (Step 1) ====== */}
      <Dialog open={showImportPreview} onOpenChange={(o) => { if (!o) { setShowImportPreview(false); setImportPreview(null); } }}>
        <DialogContent className="sm:max-w-[1400px] max-h-[92vh] overflow-y-auto" data-testid="import-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
              <FileText size={18} weight="bold" className="text-purple-600" /> Bulk Purchase Import — Review Before Import
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">Review the auto-generated product codes, batch numbers, and totals. Nothing is saved until you click Import.</DialogDescription>
          </DialogHeader>
          {importPreview && (
            <div className="space-y-3">
              {/* Counters */}
              <div className="grid grid-cols-4 gap-2">
                <Card className="p-2.5 border-2 border-gray-300 bg-gray-50"><p className="text-[10px] font-bold text-gray-700 uppercase">Total Rows</p><p className="text-2xl font-black text-gray-800 font-mono" data-testid="preview-total">{importPreview.counters.total}</p></Card>
                <Card className="p-2.5 border-2 border-emerald-300 bg-emerald-50"><p className="text-[10px] font-bold text-emerald-700 uppercase">✓ Ready</p><p className="text-2xl font-black text-emerald-700 font-mono" data-testid="preview-ready">{importPreview.counters.ready}</p></Card>
                <Card className="p-2.5 border-2 border-amber-300 bg-amber-50"><p className="text-[10px] font-bold text-amber-700 uppercase">⚠ Warnings</p><p className="text-2xl font-black text-amber-700 font-mono" data-testid="preview-warnings">{importPreview.counters.warnings}</p></Card>
                <Card className={`p-2.5 border-2 ${importPreview.counters.errors > 0 ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"}`}><p className={`text-[10px] font-bold uppercase ${importPreview.counters.errors > 0 ? "text-red-700" : "text-gray-500"}`}>✕ Errors</p><p className={`text-2xl font-black font-mono ${importPreview.counters.errors > 0 ? "text-red-700" : "text-gray-500"}`} data-testid="preview-errors">{importPreview.counters.errors}</p></Card>
              </div>
              {/* Table */}
              <div className="border border-gray-200 rounded-lg overflow-auto max-h-[52vh]">
                <table className="w-full text-[11px]">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr className="text-gray-700 font-black uppercase text-[10px]">
                      <th className="p-1.5 text-left">Status</th>
                      <th className="p-1.5 text-left">Row</th>
                      <th className="p-1.5 text-left">Date</th>
                      <th className="p-1.5 text-left">Invoice</th>
                      <th className="p-1.5 text-left">Supplier</th>
                      <th className="p-1.5 text-left">Product</th>
                      <th className="p-1.5 text-right">Qty</th>
                      <th className="p-1.5 text-left">Unit</th>
                      <th className="p-1.5 text-right">Rate</th>
                      <th className="p-1.5 text-right">GST%</th>
                      <th className="p-1.5 text-left">HSN</th>
                      <th className="p-1.5 text-left">Prod Code</th>
                      <th className="p-1.5 text-left">Batch</th>
                      <th className="p-1.5 text-right">Subtotal</th>
                      <th className="p-1.5 text-right">GST Amt</th>
                      <th className="p-1.5 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.rows.map((r) => {
                      const bg = r.status === "ready" ? "bg-emerald-50/40" : r.status === "warning" ? "bg-amber-50/40" : "bg-red-50/40";
                      const icon = r.status === "ready" ? "✓" : r.status === "warning" ? "⚠" : "✕";
                      const iconClass = r.status === "ready" ? "text-emerald-600" : r.status === "warning" ? "text-amber-600" : "text-red-600";
                      return (
                        <tr key={r.row} className={`${bg} border-b border-gray-100 hover:brightness-95`} data-testid={`preview-row-${r.row}`}>
                          <td className="p-1.5"><span className={`font-black text-base ${iconClass}`}>{icon}</span></td>
                          <td className="p-1.5 font-mono text-gray-500">{r.row}</td>
                          <td className="p-1.5">{r.date || "—"}</td>
                          <td className="p-1.5 font-mono">{r.invoice_no || "—"}</td>
                          <td className="p-1.5">
                            <span className={r.supplier_status === "new" ? "text-purple-700 font-bold" : "text-gray-900"}>{r.supplier || "—"}</span>
                            {r.supplier_status === "new" && <span className="ml-1 text-[9px] bg-purple-100 text-purple-700 rounded px-1 font-bold">NEW</span>}
                          </td>
                          <td className="p-1.5">
                            <span className={r.product_status === "new" ? "text-purple-700 font-bold" : "text-gray-900"}>{r.product_name || "—"}</span>
                            {r.product_status === "new" && <span className="ml-1 text-[9px] bg-purple-100 text-purple-700 rounded px-1 font-bold">NEW</span>}
                          </td>
                          <td className="p-1.5 text-right font-mono">{r.qty}</td>
                          <td className="p-1.5">{r.unit}</td>
                          <td className="p-1.5 text-right font-mono">{r.rate}</td>
                          <td className="p-1.5 text-right font-mono">{r.gst_percent}%</td>
                          <td className="p-1.5 font-mono text-gray-500">{r.hsn_code || "—"}</td>
                          <td className="p-1.5 font-mono text-indigo-700 font-bold">{r.product_code || "—"}</td>
                          <td className="p-1.5 font-mono text-blue-700 font-bold text-[10px]">{r.batch_code || "—"}</td>
                          <td className="p-1.5 text-right font-mono">{r.subtotal}</td>
                          <td className="p-1.5 text-right font-mono">{r.gst_amount}</td>
                          <td className="p-1.5 text-right font-mono font-bold text-emerald-700">{r.total}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Messages panel */}
              {importPreview.rows.some((r) => r.messages.length > 0) && (
                <details className="border border-gray-200 rounded-lg overflow-hidden">
                  <summary className="bg-gray-50 px-3 py-2 text-xs font-black text-gray-700 uppercase cursor-pointer">Notes &amp; Warnings ({importPreview.rows.reduce((n, r) => n + r.messages.length, 0)})</summary>
                  <div className="max-h-[220px] overflow-y-auto divide-y divide-gray-100">
                    {importPreview.rows.map((r) => r.messages.map((m, mi) => (
                      <div key={`${r.row}-${mi}`} className="px-3 py-1 text-[11px] flex items-start gap-2">
                        <span className={`flex-shrink-0 font-black text-base ${r.status === "error" ? "text-red-600" : r.status === "warning" ? "text-amber-600" : "text-emerald-600"}`}>{r.status === "error" ? "✕" : r.status === "warning" ? "⚠" : "✓"}</span>
                        <span className="text-gray-800">{m}</span>
                      </div>
                    )))}
                  </div>
                </details>
              )}
              <div className="flex items-center gap-2">
                <Button variant="outline" className="h-10" onClick={() => { setShowImportPreview(false); setImportPreview(null); }} disabled={committing} data-testid="preview-cancel">Cancel</Button>
                <Button
                  className="h-10 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-40"
                  onClick={handleCommitImport}
                  disabled={committing || (importPreview.counters.ready + importPreview.counters.warnings) === 0}
                  data-testid="preview-confirm"
                >
                  <CheckCircle size={16} className="mr-1" weight="bold" /> {committing ? "Importing…" : `Import ${importPreview.counters.ready + importPreview.counters.warnings} Valid Rows`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ====== Import Summary Dialog ====== */}
      <Dialog open={showImportSummary} onOpenChange={(o) => { if (!o) { setShowImportSummary(false); setImportSummary(null); } }}>
        <DialogContent className="sm:max-w-[700px] max-h-[92vh] overflow-y-auto" data-testid="import-summary-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
              <FileText size={18} weight="bold" className="text-emerald-600" /> Bulk Purchase Import — Summary
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">Import complete. See the counts and any errors below.</DialogDescription>
          </DialogHeader>
          {importSummary && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <Card className="p-2.5 border-2 border-emerald-300 bg-emerald-50"><p className="text-[10px] font-bold text-emerald-700 uppercase">Invoices</p><p className="text-lg font-black text-emerald-700 font-mono" data-testid="summary-invoices">{importSummary.invoices_imported}</p></Card>
                <Card className="p-2.5 border-2 border-indigo-300 bg-indigo-50"><p className="text-[10px] font-bold text-indigo-700 uppercase">Products</p><p className="text-lg font-black text-indigo-700 font-mono" data-testid="summary-products">{importSummary.products_imported}</p></Card>
                <Card className="p-2.5 border-2 border-amber-300 bg-amber-50"><p className="text-[10px] font-bold text-amber-700 uppercase">Total KG</p><p className="text-lg font-black text-amber-700 font-mono">{importSummary.total_kg}</p></Card>
                <Card className="p-2.5 border-2 border-purple-300 bg-purple-50"><p className="text-[10px] font-bold text-purple-700 uppercase">Total Value</p><p className="text-lg font-black text-purple-700 font-mono">{formatCurrency(importSummary.total_value)}</p></Card>
                <Card className="p-2.5 border-2 border-blue-300 bg-blue-50"><p className="text-[10px] font-bold text-blue-700 uppercase">Total GST</p><p className="text-lg font-black text-blue-700 font-mono">{formatCurrency(importSummary.total_gst)}</p></Card>
              </div>
              {importSummary.error_count > 0 ? (
                <div className="border-2 border-red-200 rounded-lg overflow-hidden" data-testid="import-errors">
                  <div className="bg-red-100 px-3 py-2 text-xs font-black text-red-800 uppercase flex items-center justify-between">
                    <span>Errors ({importSummary.error_count})</span>
                    <span className="text-[10px] font-normal text-red-600">Fix these rows and re-upload</span>
                  </div>
                  <div className="divide-y divide-red-100 max-h-[280px] overflow-y-auto">
                    {importSummary.errors.map((err, i) => (
                      <div key={i} className="px-3 py-1.5 text-xs bg-white flex items-start gap-2" data-testid={`error-row-${i}`}>
                        <span className="w-12 flex-shrink-0 text-[10px] font-black text-red-700 bg-red-50 rounded px-1.5 py-0.5 text-center">Row {err.row}</span>
                        <span className="text-gray-800">{err.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-center gap-2">
                  <CheckCircle size={18} weight="bold" className="text-emerald-600" />
                  <span>All rows imported successfully! Inventory, batches, and supplier ledgers have been updated.</span>
                </div>
              )}
              <Button className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" onClick={() => { setShowImportSummary(false); setImportSummary(null); }} data-testid="close-summary">Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
